import { useEffect } from 'react';
import { useSiteSettings } from '@/context/SiteSettingsContext';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const SITE_NAME = 'NKP VENTURES';
const DEFAULT_TITLE = 'NKP VENTURES — Premium BPO Partner for International Businesses';
const DEFAULT_DESC = 'NKP VENTURES provides premium Business Process Outsourcing services for businesses in the UK, US, Canada, Europe, Australia and New Zealand. Expert admin, customer support, finance, HR and operational support.';

function setMeta(name, content, isProperty = false) {
  if (content === undefined || content === null || content === '') return;
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href, extraAttrs = {}) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    Object.entries(extraAttrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(id, data) {
  if (!data) return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = typeof data === 'string' ? data : JSON.stringify(data);
}

/**
 * SEOHead — meta tag manager for each public page.
 * Consumes site-wide defaults from SiteSettingsContext automatically.
 */
export function SEOHead({ title, description, canonical, ogImage, type = 'website', noIndex = false, structuredData }) {
  const s = useSiteSettings();

  useEffect(() => {
    const siteName = s.brand_name || SITE_NAME;
    const defaultTitle = s.seo_title_default || DEFAULT_TITLE;
    const defaultDesc = s.seo_description_default || DEFAULT_DESC;

    const fullTitle = !title
      ? defaultTitle
      : title.includes(siteName) ? title : `${title} | ${siteName}`;
    const desc = description || defaultDesc;
    const url = canonical || s.canonical_url || (typeof window !== 'undefined' ? window.location.href : '');

    document.title = fullTitle;

    setMeta('description', desc);
    setMeta('robots', noIndex ? 'noindex, nofollow' : (s.robots_setting || 'index, follow, max-snippet:-1, max-image-preview:large'));
    if (s.meta_keywords) setMeta('keywords', s.meta_keywords);

    setLink('canonical', url);

    // Open Graph
    setMeta('og:title', s.og_title || fullTitle, true);
    setMeta('og:description', s.og_description || desc, true);
    setMeta('og:type', type, true);
    setMeta('og:url', url, true);
    setMeta('og:site_name', siteName, true);
    const resolvedOgImage = ogImage || s.og_image;
    if (resolvedOgImage) setMeta('og:image', resolvedOgImage, true);

    // Twitter Card
    setMeta('twitter:card', s.twitter_card || 'summary_large_image');
    setMeta('twitter:title', s.twitter_title || fullTitle);
    setMeta('twitter:description', s.twitter_description || desc);
    const resolvedTwitterImage = ogImage || s.twitter_image;
    if (resolvedTwitterImage) setMeta('twitter:image', resolvedTwitterImage);

    // Site verification tags
    if (s.google_verification) setMeta('google-site-verification', s.google_verification);
    if (s.bing_verification) setMeta('msvalidate.01', s.bing_verification);
    if (s.facebook_verification) setMeta('facebook-domain-verification', s.facebook_verification);

    // Dynamic favicon
    const faviconUrl = s.favicon_url || (s.favicon_id ? `/api/media/${s.favicon_id}` : null);
    if (faviconUrl) {
      setLink('icon', faviconUrl, { type: 'image/png' });
      setLink('shortcut icon', faviconUrl);
    }

    // Custom structured data JSON-LD from admin (overrides per-page)
    if (s.structured_data_json) {
      try {
        const parsed = JSON.parse(s.structured_data_json);
        setJsonLd('pkp-custom-schema', parsed);
      } catch (_) {}
    }

    // Per-page structured data
    if (structuredData) setJsonLd('pkp-structured-data', structuredData);
  }, [title, description, canonical, ogImage, type, noIndex, structuredData, s]);

  return null;
}

// ─── Dynamic schema builders (use settings for dynamic values) ─────

export function buildOrganizationSchema(s = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": s.brand_name || "NKP VENTURES",
    "legalName": s.company_name || "PKRUPARTH ENTERPRISE PVT. LTD.",
    "url": BASE_URL,
    "logo": s.primary_logo_url ? `${BASE_URL}${s.primary_logo_url}` : `${BASE_URL}/android-chrome-192x192.png`,
    "description": s.seo_description_default || DEFAULT_DESC,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": (s.address || "A/7 Nirkunj, Satellite").split(",")[0],
      "addressLocality": "Ahmedabad",
      "addressRegion": "Gujarat",
      "postalCode": "380015",
      "addressCountry": "IN"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": s.phone || "+91-99251-90747",
      "email": s.email || "pkruparthenterprisepvtltd@gmail.com",
      "contactType": "customer service"
    },
    "sameAs": [
      s.social_linkedin, s.social_twitter, s.social_facebook,
      s.social_instagram, s.social_youtube
    ].filter(Boolean)
  };
}

// Keep for backward compat (static default version)
export const ORGANIZATION_SCHEMA = buildOrganizationSchema({});

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "NKP VENTURES",
  "url": BASE_URL,
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${BASE_URL}/insights?q={search_term_string}`,
    "query-input": "required name=search_term_string"
  }
};

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "name": item.name,
      "item": `${BASE_URL}${item.path}`
    }))
  };
}

export function serviceSchema(name, description, url) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": name,
    "name": name,
    "description": description,
    "provider": { "@type": "Organization", "name": "NKP VENTURES", "url": BASE_URL },
    "url": `${BASE_URL}${url}`,
    "areaServed": ["GB", "US", "CA", "AU", "NZ", "EU"]
  };
}

export function articleSchema({ title, description, slug, publishedAt, authorName = 'NKP VENTURES Editorial Team' }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "author": { "@type": "Organization", "name": authorName },
    "publisher": { "@type": "Organization", "name": "NKP VENTURES", "url": BASE_URL },
    "url": `${BASE_URL}/insights/${slug}`,
    "datePublished": publishedAt,
    "mainEntityOfPage": `${BASE_URL}/insights/${slug}`
  };
}
